/**
 * Parent PIN lockout. Pure: it is given the failure count and the time of the
 * last failure, and says until when the keypad is closed.
 *
 * The point is not to stop a determined attacker — the tablet is in the kitchen —
 * but to make guessing slow and, above all, *visible*: every failure is an event
 * the parents can see.
 */

export interface LockoutPolicy {
  /** Failures tolerated before the first lock. */
  readonly threshold: number
  readonly baseMs: number
  readonly factor: number
  readonly maxMs: number
}

export const DEFAULT_LOCKOUT: LockoutPolicy = {
  threshold: 5,
  baseMs: 30_000,
  factor: 2,
  maxMs: 3_600_000,
}

/** How long the lock lasts after `consecutiveFailures` failures. Zero if none. */
export function lockoutDurationMs(
  consecutiveFailures: number,
  policy: LockoutPolicy = DEFAULT_LOCKOUT,
): number {
  if (consecutiveFailures < policy.threshold) return 0
  const steps = consecutiveFailures - policy.threshold
  return Math.min(policy.maxMs, policy.baseMs * policy.factor ** steps)
}

/** The instant the keypad reopens, or null when it is not locked. */
export function lockedUntil(
  consecutiveFailures: number,
  lastFailureAt: Date,
  policy: LockoutPolicy = DEFAULT_LOCKOUT,
): Date | null {
  const duration = lockoutDurationMs(consecutiveFailures, policy)
  return duration === 0 ? null : new Date(lastFailureAt.getTime() + duration)
}

export function isLocked(
  consecutiveFailures: number,
  lastFailureAt: Date,
  now: Date,
  policy: LockoutPolicy = DEFAULT_LOCKOUT,
): boolean {
  const until = lockedUntil(consecutiveFailures, lastFailureAt, policy)
  return until !== null && now.getTime() < until.getTime()
}

export function remainingLockMs(
  consecutiveFailures: number,
  lastFailureAt: Date,
  now: Date,
  policy: LockoutPolicy = DEFAULT_LOCKOUT,
): number {
  const until = lockedUntil(consecutiveFailures, lastFailureAt, policy)
  if (until === null) return 0
  return Math.max(0, until.getTime() - now.getTime())
}
